import { DEVELOPERS, SALARY_CONFIG } from './data/projects';

// ─── DATE HELPERS ───────────────────────────────────────────────────────────
export function monthsBetween(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
}

export function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function formatDateFull(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function isProjectActive(project) {
  const now = new Date();
  const start = new Date(project.startDate);
  const end = new Date(project.endDate);
  return now >= start && now <= end;
}

export function getProjectProgress(project) {
  if (project.status === 'completed') return 100;
  if (project.status === 'planned') return 0;
  if (project.status === 'cancelled') return 0;
  const now = new Date();
  const start = new Date(project.startDate);
  const end = new Date(project.endDate);
  if (now < start) return 0;
  if (now > end) return 100;
  return Math.round(((now - start) / (end - start)) * 100);
}

// ─── SALARY CALCULATIONS ────────────────────────────────────────────────────
export function getDevById(id, devs) {
  return (devs || DEVELOPERS).find(d => d.id === id);
}

export function getMonthlyTeamCost(project, devs) {
  return project.assignedDevIds.reduce((sum, devId) => {
    const dev = getDevById(devId, devs);
    return sum + (dev ? dev.monthlySalary : 0);
  }, 0);
}

export function getRawBurn(project, devs) {
  return getMonthlyTeamCost(project, devs) * monthsBetween(project.startDate, project.endDate);
}

export function getTotalBurnWithOverhead(project, devs) {
  return Math.round(getRawBurn(project, devs) * (1 + SALARY_CONFIG.overheadPct));
}

export function getTeamSize(project) {
  return project.assignedDevIds.length;
}

// ─── COST-SHARING CALCULATIONS ───────────────────────────────────────────────
// When a developer works on multiple projects in the same month, their salary
// is divided equally across those projects for that month.

function _iterMonths(project, callback) {
  const pStart = new Date(project.startDate);
  const pEnd   = new Date(project.endDate);
  const cur    = new Date(pStart.getFullYear(), pStart.getMonth(), 1);
  const last   = new Date(pEnd.getFullYear(),   pEnd.getMonth(),   1);
  while (cur <= last) {
    callback(new Date(cur), new Date(cur.getFullYear(), cur.getMonth() + 1, 0));
    cur.setMonth(cur.getMonth() + 1);
  }
}

function _devActiveInMonth(dev, mStart, mEnd) {
  if (new Date(dev.startDate) > mEnd) return false;
  if (dev.endDate && new Date(dev.endDate) < mStart) return false;
  return true;
}

export function getProjectRawShared(project, allProjects, devs) {
  const devList = devs || DEVELOPERS;
  let total = 0;
  _iterMonths(project, (mStart, mEnd) => {
    for (const devId of project.assignedDevIds) {
      const dev = devList.find(d => d.id === devId);
      if (!dev || !_devActiveInMonth(dev, mStart, mEnd)) continue;
      const count = allProjects.filter(p =>
        p.type !== 'main' &&
        p.assignedDevIds.includes(devId) &&
        new Date(p.startDate) <= mEnd &&
        new Date(p.endDate)   >= mStart
      ).length;
      total += dev.monthlySalary / Math.max(1, count);
    }
  });
  return Math.round(total);
}

export function getProjectTotalShared(project, allProjects, devs) {
  return Math.round(getProjectRawShared(project, allProjects, devs) * (1 + SALARY_CONFIG.overheadPct));
}

export function getDevProjectContribution(devId, project, allProjects, devs) {
  const devList = devs || DEVELOPERS;
  const dev = devList.find(d => d.id === devId);
  if (!dev) return 0;
  let total = 0;
  _iterMonths(project, (mStart, mEnd) => {
    if (!_devActiveInMonth(dev, mStart, mEnd)) return;
    const count = allProjects.filter(p =>
      p.type !== 'main' &&
      p.assignedDevIds.includes(devId) &&
      new Date(p.startDate) <= mEnd &&
      new Date(p.endDate)   >= mStart
    ).length;
    total += dev.monthlySalary / Math.max(1, count);
  });
  return Math.round(total);
}

export function getPortfolioTotals(projects, devs) {
  const billable = projects.filter(p => p.type !== 'main');
  const devList  = devs || DEVELOPERS;
  return {
    total:      projects.length,
    completed:  projects.filter(p => p.status === 'completed').length,
    inProgress: projects.filter(p => p.status === 'in-progress').length,
    planned:    projects.filter(p => p.status === 'planned').length,
    onHold:     projects.filter(p => p.status === 'on-hold').length,
    cancelled:  projects.filter(p => p.status === 'cancelled').length,
    totalRaw:      billable.reduce((s, p) => s + getProjectRawShared(p, projects, devs), 0),
    totalOverhead: billable.reduce((s, p) => s + getProjectTotalShared(p, projects, devs), 0),
    totalMonthlyPayroll: devList.filter(d => d.status === 'Active').reduce((s, d) => s + d.monthlySalary, 0),
  };
}

export const AED_RATE = 14; // 1 AED = 14 L.E

export function formatCurrency(n) {
  return 'L.E ' + Number(n).toLocaleString('en-US');
}

export function formatAED(egp) {
  return 'AED ' + Math.round(Number(egp) / AED_RATE).toLocaleString('en-US');
}

// ─── STATUS CONFIG ───────────────────────────────────────────────────────────
export const STATUS_CONFIG = {
  'completed':   { label: 'Completed',   color: '#22c55e', bg: '#dcfce7', text: '#166534', dot: '🟢' },
  'in-progress': { label: 'In Progress', color: '#3b82f6', bg: '#dbeafe', text: '#1e40af', dot: '🔵' },
  'planned':     { label: 'Planned',     color: '#a855f7', bg: '#f3e8ff', text: '#6b21a8', dot: '🟣' },
  'on-hold':     { label: 'On Hold',     color: '#f59e0b', bg: '#fef3c7', text: '#92400e', dot: '🟡' },
  'cancelled':   { label: 'Cancelled',   color: '#ef4444', bg: '#fee2e2', text: '#991b1b', dot: '🔴' },
};

export const TYPE_CONFIG = {
  'main':       { label: 'Main Project', icon: '🔷' },
  'sub':        { label: 'Sub-Module',   icon: '  ↳' },
  'standalone': { label: 'Standalone',   icon: '◼' },
};

// ─── GANTT HELPERS ───────────────────────────────────────────────────────────
export const GANTT_START = new Date('2024-01-01');
export const GANTT_END   = new Date('2026-12-31');

export function ganttMonths() {
  const months = [];
  const cur = new Date(GANTT_START);
  while (cur <= GANTT_END) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

export function ganttOffsetPct(dateStr) {
  const d = new Date(dateStr);
  const total = GANTT_END - GANTT_START;
  const offset = Math.max(0, d - GANTT_START);
  return Math.min(100, (offset / total) * 100);
}

export function ganttWidthPct(startStr, endStr) {
  const s = new Date(Math.max(new Date(startStr), GANTT_START));
  const e = new Date(Math.min(new Date(endStr), GANTT_END));
  const total = GANTT_END - GANTT_START;
  return (Math.max(0, e - s) / total) * 100;
}
